import {Injectable} from '@angular/core';
import {ActiveDevice, ApiDevice, Device, DeviceType, NeedSyncChangeData} from '../models/device';
import {AuthService, UserRole} from '../auth/auth.service';
import {HttpClient} from '@angular/common/http';
import {environment} from 'src/environments/environment';
import {catchError, concatMap, delay, distinctUntilChanged, filter, map, multicast, refCount, repeatWhen, skip, take, takeUntil, takeWhile, tap} from 'rxjs/operators';
import {BehaviorSubject, EMPTY, Observable, ReplaySubject, throwError, timer} from 'rxjs';
import {NGXLogger} from 'ngx-logger';
import {CookieService} from './cookie.service';
import {LicenceService} from './licence.service';
import {Feature, featureConstraints} from '../constants/feature-constants';
import {Router} from '@angular/router';
import {DeviceLicence} from '@app/core/models/device-licence';
import {SyncService} from '@app/core/services/sync.service';
import {ModalService} from '@app/core/services/modal.service';
import {MessageBoxIcon, ModalButton, ModalButtons} from '@app/core/models/modal-ref';
import {ErrorCode} from '@app/core/constants/error-codes';
import { ScgHttpWrapper } from '../injectables/scg-http-wrapper';

@Injectable({
  providedIn: 'root'
})
export class DeviceService {

  private activeDeviceSubject = new BehaviorSubject<ActiveDevice>(null);
  /**
   * Multicasted observables which emit when the online status of a device changes.
   * Because they are based on BehaviorSubjects, the last status value will always be saved.
   *
   * key = deviceId
   */
  private isOnlineChangeObservables: Map<string, Observable<boolean>> = new Map();

  private _devices: Device[] = [];

  /** True after the initial device list has been fetched (regardless of whether devices were found) */
  public isInitialized = false;

  private initializedSubject = new ReplaySubject<void>(1);

  /** Emits once when the device list has been fetched for the first time after login */
  public get onInitialized(): Observable<void> {
    return this.initializedSubject.asObservable();
  }

  /**
   * Emits if the active device or user was changed.
   *
   * On subscribing, the current active user is returned.
   */
  public get onDeviceSwitch(): Observable<ActiveDevice> {
    return this.activeDeviceSubject
      .asObservable()
      .pipe(
        filter(activeDevice => activeDevice != null)
      );
  }

  public get devices(): Device[] {
    return this._devices;
  }

  /**
   * The currently active device (i.e. the device which is selected by the user to view and change settings and limits).
   * May be null if there is no active device (e.g. on startup).
   */
  public get activeDevice(): ActiveDevice|null {
    return this.activeDeviceSubject.value;
  }

  constructor(
    private auth: AuthService,
    private licenceService: LicenceService,
    private cookie: CookieService,
    private sync: SyncService,
    private http: HttpClient,
    private router: Router,
    private modal: ModalService,
    private logger: NGXLogger,
    private scgHttp: ScgHttpWrapper
  ) { }

  /**
   * Needs to be called at the start of the app.
   *
   * **Never** move this init stuff to the constructor or you will get circular dependencies because of the HTTP interceptors ;)
   */
  public init() {
    // Subscribe to onLogin event to get notified when user is logged in
    this.auth.onLogin.subscribe({
      next: (apiLogin) => {
        this.initDevices(apiLogin);
      }
    });

    this.auth.onLogout.subscribe({
      next: () => {
        // Delete everything
        this.activeDeviceSubject.next(null);
        this._devices = [];
        this.isInitialized = false;
        this.initializedSubject = new ReplaySubject<void>(1);

        /*
         * Delete all cached online change observables.
         * Hopefully prevents rare errors which occur when user logs in again after getting logged out because of timeout
         * (in these cases, the user was not logged out after making these requests (seems like no 401 was returned),
         * but was logged out after other requests. All in all, a very strange error.
         */
        this.isOnlineChangeObservables.clear();
      }
    });
  }

  public reloadDeviceList()
  {
    this._devices = [];
    this.initDevices(false);
  }

  /**
   * Switch the currently active device
   * @param device    Device to switch to
   * @param username  Name of the user to switch to if the device supports users
   */
  public switchDevice(device: Device, username?: string) {
    // Check for errors
    if (device.type === DeviceType.CC && (device.users == null || device.users.length === 0)) {
      const msg = `Switching to device (${device.name}) which does not have any users is not possible`;
      this.logger.error(msg);
      this.modal.showErrorMessageBox({
        deviceName: device.name,
        username,
        errorCode: ErrorCode.SwitchDeviceNoUsers,
        msg
      });
      return;
    }

    // Special case use is no more available but we have second one
    if (device.type === DeviceType.CC && username != null && !device.users.includes(username) && device.users.length > 0) {
      username = device.users[0];
    }

    if (device.type === DeviceType.CC && username != null && !device.users.includes(username)) {
      const msg = `Cannot switch device because the given user "${username}" does not exist on device "${device.name}"`;
      this.logger.error(msg);
      this.modal.showErrorMessageBox({
        deviceName: device.name,
        username,
        errorCode: ErrorCode.SwitchDeviceUserDoesNotExist,
        msg
      });
      return;
    }
    // No errors so far


    /* Disabled, because we need device switch be removing or adding users in Settings -> Win Users to refresh userlist dropdown!
    if (this.activeDevice?.device.id === device.id && this.activeDevice?.username === username) {
      // Prevent unneccesary device switch
      this.logger.info('switchDevice() was called but cancelled because the given device/user is already the active device');
      return;
    }*/



    this.cookie.setCookie({ name: 'pcId', value: device.id, path: '/', sameSite: 'Lax', secure: true });
    const activeDevice = new ActiveDevice(device);

    this.logger.info('Switched device to', device);

    if (device.type === DeviceType.CC && username != null) {
      activeDevice.username = username;
      this.cookie.setCookie({ name: 'user', value: username, path: '/', sameSite: 'Lax', secure: true });

      this.logger.info('Switched to user', username);
    } else if (device.type === DeviceType.CC && username == null) {
      // Set default user if no user was given
      const defaultUsername = device.users[0];
      this.logger.warn(`No username given but we switch to CC device. Falling back to default user "${defaultUsername}"`);
      activeDevice.username = defaultUsername;
      this.cookie.setCookie({ name: 'user', value: defaultUsername, path: '/', sameSite: 'Lax', secure: true });

      this.logger.info('Switched to user', device.users[0]);
    } else {
        activeDevice.username = undefined;
        this.cookie.deleteCookie('user');
    }

    // Navigate to dashboard if the new device does not support the features that are required by the activated route
    if (!this.isActivatedRouteSupported(activeDevice.device)) {
      this.router.navigate(['']);
    }

    this.activeDeviceSubject.next(activeDevice);

    // Defektes Gerät (deviceType=90): User informieren, dass das Gerät geprüft/neu installiert werden muss.
    // Dieser Pfad wird nur erreicht, wenn switchDevice ohne UI-Sperre aufgerufen wird (z.B. initialer Switch nach Login).
    // Die Dropdown-Auswahl blockt den Switch bereits vorher und zeigt dasselbe Modal.
    if (device.type === DeviceType.Invalid) {
      this.modal.showMessageBox({
        title: 'COMMON.DEVICE_INVALID_TITLE',
        text: 'COMMON.DEVICE_INVALID_TEXT',
        icon: MessageBoxIcon.Warning,
        buttons: ModalButtons.Ok,
        defaultButton: ModalButton.Ok
      });
    }

    if (this.auth.role === UserRole.Default || this.auth.role === UserRole.Parents) {
      // Get the licence AFTER the cookie was set, to make sure the correct pcId is sent
      // Also, needs to be called AFTER the activeDeviceSubject was updated
      this.reloadActiveDeviceLicence().subscribe();

      // Check whether there are changes that were not synced yet
      if (activeDevice.device.type === DeviceType.CB) {
        this.sync.needSyncCb(activeDevice.device.id).subscribe(needSync => {
          // Set needSync with localCause = FALSE
          activeDevice.device.setNeedSync(needSync, false);
        });
      } else if (activeDevice.device.type === DeviceType.CC) {
        this.sync.needSyncCc(activeDevice.device.id).subscribe(needSync => {
          // Set needSync with localCause = FALSE
          activeDevice.device.setNeedSync(needSync, false);
        });
      }
    }
  }

  public reloadActiveDeviceLicence(): Observable<DeviceLicence> {
    if (!this.activeDevice) {
      return throwError('There is no active device!');
    }

    return this.licenceService.getLicence(true)
      .pipe(
        // Cancel loading if device was switched in the meantime (skip 2 because
        // when called from switchDevice, we need to skip the default value and the emit that is invoked from switchDevice itself)
        takeUntil(this.onDeviceSwitch.pipe(skip(2))),
        catchError(error => {
          this.logger.error('Licence error', error);
          return throwError(error);
        }),
        tap((licence) => {
          if (this.activeDevice) {
            this.activeDeviceSubject.value.licence = licence;
            this.logger.info('getlicence=', licence);
            this.licenceService.openLicenceScreen(licence, this.activeDevice.device.id);
          }
        })
      );
  }

  private isChromebook(device?: Device): boolean {
    if (device == null) return false;
    if (device.manufacturer != null && device.manufacturer !== '' && device.manufacturer.indexOf('Chromebook') !== -1) return true;
    return false;
  }

  private isChromeTV(device?: Device): boolean {
    if (device == null) return false;
    if (device.manufacturer != null && device.manufacturer !== '' && device.manufacturer.indexOf('AndroidTV') !== -1) return true;
    return false;
  }

  /**
   * Returns true if the given device supports the given feature.
   *
   * If no device is given, the activeDevice is used. If no device is given and there is no active device, false is returned.
   */
  public isFeatureSupported(feature: Feature, device?: Device): boolean {
    if (!device && this.activeDevice?.device) {
      device = this.activeDevice.device;
    } else if (!device && !this.activeDevice?.device) {
      return false;
    }

    const constraints = featureConstraints[feature];

    switch (device.type) {
      case DeviceType.CB:
        if (constraints.CbUnsupported) {
          return false;
        }

        if (this.isChromebook(device) && constraints.ChromeOSUnsupported) {
            return false;
        }

        if (this.isChromeTV(device) && constraints.ChromeOSUnsupported) {
          return false;
        }

        if (!this.isInVersionRange(device.version, constraints.fromCbVersion, constraints.toCbVersion)) {
          return false;
        }
        if (!this.isInVersionRange(device.osVersion, constraints.fromAndroidVersion, constraints.toAndroidVersion)) {
          return false;
        }

        // If we reach this point, the device is supported
        return true;

      case DeviceType.CC:
        if (constraints.CcUnsupported) {
          return false;
        }

        if (!this.isInVersionRange(device.version, constraints.fromCcVersion, constraints.toCcVersion)) {
          return false;
        }

        if (!this.isInVersionRange(device.osVersion, constraints.fromWindowsVersion, constraints.toWindowsVersion)) {
          return false;
        }

        // If we reach this point, the device is supported
        return true;

      default:
        this.logger.error(`Cannot check whether feature ${feature} is supported because there is no handler`);
        return false;
    }
  }

  /**
   * Returns true if the given device supports at least one of the features that were defined by the activated route
   * (or a parent route from the activated route).
   * Each route can define an array of features of which at least one feature must be supported.
   * If no feature is supported, the loading of the route should be prevented.
   *
   * If no device is given, the activeDevice is used. If there is no active device, false is returned.
   */
  public isActivatedRouteSupported(device?: Device) {
    // Go to deepest route
    let route = this.router.routerState.root.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // Check if any features are defined in the requested route itself or any parent route
    let features = route.data.features || [];
    let parent = route.parent;

    while (features.length === 0 && parent) {
      features = parent.data.features || [];
      parent = parent.parent;
    }

    if (features.length > 0) {
      const result = features.some((feature: Feature) => this.isFeatureSupported(feature, device));
      return result;
    }

    // Allow the route if no feature was defined on any parent route or the route itself
    return true;
  }

  /**
   * Deletes the device with the given id (or the activeDevice if no id is given).
   *
   * **Note**: When the active device is deleted successfully, the device should be switched or the user logged out
   */
  public deleteDevice(deviceId?: string): Observable<any> {
    return this.scgHttp.withDevice(deviceId).get<any>(environment.apiUrl + 'action/deletedevice');
  }

  public deleteDeviceUnknown(): Observable<any> {
    return this.scgHttp.withDevice('UNKNOWN').get<any>(environment.apiUrl + 'action/deletedeviceunknown');
  }

  /**
   * Resets the name of the activeDevice and returns the new name
   */
  public resetDeviceName(): Observable<string> {
    // Get and update name
    const getAndUpdateName = this.getDeviceName().pipe(
      tap((name) => this.activeDevice.device.name = name)
    );

    return this.http.get<null>(environment.apiUrl + 'action/resetdevicename').pipe(
      concatMap(
        () => getAndUpdateName
      )
    );
  }

  /**
   * Gets the name of the activeDevice from the API
   */
  public getDeviceName(): Observable<string> {
    return this.http.get<string>(environment.apiUrl + 'action/getdevicename');
  }

  public setDeviceName(devicename: string): Observable<any> {
    const idStr = 'name';
    const postVal = {};
    postVal[idStr] = devicename;
    return this.http.post<any>(environment.apiUrl + 'action/setdevicename' , postVal).pipe(
      tap(() => {
        this.activeDevice.device.name = devicename;
      })
    );
  }

  /**
   * Emits when the online status of the given device changes. Also emits the current online status on subscribing.
   *
   * Emits true if the device is online, false otherwise.
   */
  public isOnlineChange(deviceId: string): Observable<boolean> {
    if (this.isOnlineChangeObservables.get(deviceId)) {
      return this.isOnlineChangeObservables.get(deviceId);
    } else {
      const device = this.devices.find(dev => dev.id === deviceId);
      if (!device) {
        this.logger.error('Cannot track online change because the given deviceId does not exist');
        return EMPTY;
      }

      const onlineChangeSubject = new BehaviorSubject<boolean>(false);

      const isOnlineCbSource = this.sync.getIsOnlineCB(deviceId).pipe(
        repeatWhen(notifier => notifier.pipe(delay(15000))),
        takeUntil(this.auth.onLogout)
      );

      const isOnlineCcSource = this.sync.getIsOnlineCC(deviceId).pipe(
        repeatWhen(notifier => notifier.pipe(delay(15000))),
        map(isOnline => isOnline.success),
        takeUntil(this.auth.onLogout)
      );

      // Automatically subscribe and unsubscribe to source Observable based on whether there are any subscribers
      let refCounted: Observable<boolean> = EMPTY;
      if (device.type === DeviceType.CB) {
        refCounted = isOnlineCbSource
          .pipe(
            multicast(onlineChangeSubject),
            refCount(),
            distinctUntilChanged()
          );
      } else if (device.type === DeviceType.CC) {
        refCounted = isOnlineCcSource
          .pipe(
            multicast(onlineChangeSubject),
            refCount(),
            distinctUntilChanged()
          );
      }

      this.isOnlineChangeObservables.set(deviceId, refCounted);
      return refCounted;
    }
  }

  /**
   * Begin to work. Can't start before user is logged in
   */
  private initDevices(apiLogin: boolean) {
    if (this.auth.role !== UserRole.Report) {
      this.getDevices().subscribe({
        next: (devices) => {
          this._devices = devices;

          for (const device of devices) {
            // Handle needSync change for each device
            device.needSyncChange.subscribe(needSync => {
              this.onDeviceNeedSyncChange(device, needSync);
           });
          }

          this.initialDeviceSwitch(apiLogin);
        }
      });
    } else {
      // In report role we only fetch the initialActiveDevice because we don't have permission to fetch the others
      const initialActiveDevice = this.auth.initialActiveDevice;
      this.getDevice(initialActiveDevice.pcId).subscribe(device => {
        this._devices = [device];

        // No sync stuff for report role

        this.initialDeviceSwitch(apiLogin);
      });
    }
  }

  /**
   * Perform initial device switch after login
   */
  private initialDeviceSwitch(apiLogin: boolean) {
    this.isInitialized = true;
    this.initializedSubject.next();

    if (this.devices.length === 0) {
      this.router.navigate(['devicenotfound']);
      return;
    }

    let success = false;

    if (apiLogin) {
      // Switch to device given by AuthService
      if (this.auth.initialActiveDevice) {
        // Case-insensitive match: p2 in portal-login.ashx is passed through the backend
        // unchanged, so if the caller used wrong casing we still want to find the device.
        const wantedId = this.auth.initialActiveDevice.pcId?.toLowerCase();
        const initialDevice = this.devices.find(device => device.id?.toLowerCase() === wantedId);

        if (initialDevice && this.auth.initialActiveDevice.user != null) {
          this.switchDevice(initialDevice, this.auth.initialActiveDevice.user);
          success = true;
        } else if (initialDevice) {
          this.switchDevice(initialDevice);
          success = true;
        }
      }
    } else {
      const pcId = this.cookie.getCookie('pcId');
      const user = this.cookie.getCookie('user');

      if (pcId) {
        const wantedId = pcId.toLowerCase();
        const foundDevice = this.devices.find(device => device.id?.toLowerCase() === wantedId);

        if (foundDevice) {
          // User is ignored if foundDevice does not have users
          this.switchDevice(foundDevice, user);
          success = true;
        }
      }

      if (!success) {
        this.logger.warn('No device available to switch to via cookie');
      }
    }

    // Fallback: nothing matched — pick the device with the most recent lastHistory
    // so the user at least lands on the most recently active device instead of an
    // arbitrary first entry.
    if (!success && this.devices.length > 0) {
      this.logger.warn('Using fallback to set initial device because nothing was given by AuthService or cookie');

      const fallbackDevice = this.devices.reduce((best, current) =>
        (current.lastHistory ?? '') > (best.lastHistory ?? '') ? current : best
      );

      if (fallbackDevice.users && fallbackDevice.users.length > 0) {
        this.switchDevice(fallbackDevice, fallbackDevice.users[0]);
      } else {
        this.switchDevice(fallbackDevice);
      }
    } else if (!success && this.devices.length === 0) {
      this.logger.info('No device available to switch to');
    }
  }

  /**
   * Get devices from API
   */
  private getDevices(): Observable<Device[]> {
    return this.http.get<ApiDevice[]>(environment.apiUrl + 'service/devices').pipe(
      map<any, ApiDevice[]>(response => response ?? []),
      map<ApiDevice[], Device[]>(apiDevices => apiDevices.map(apiDevice => new Device(apiDevice))),
    );
  }

  /**
   * Get device from API
   */
  private getDevice(deviceId: string): Observable<Device> {
    return this.http.get<ApiDevice>(environment.apiUrl + 'service/device/' + deviceId).pipe(
      map<ApiDevice, Device>(apiDevice => new Device(apiDevice))
    );
  }

  private onDeviceNeedSyncChange(device: Device, needSync: NeedSyncChangeData) {
    // If the change has no local cause, we skip this
    if (needSync.needSync && needSync.localCause) {
      this.logger.info(`Device "${device.name}" needs sync`);
      let needSyncObservable: Observable<never|boolean> = EMPTY;

      if (device.type === DeviceType.CB) {
        this.sync.syncCb(device.id).subscribe();

        needSyncObservable = this.sync.needSyncCb(device.id);
      } else if (device.type === DeviceType.CC) {
        this.sync.syncCc(device.id).subscribe();

        needSyncObservable = this.sync.needSyncCc(device.id);
      }

      // Check whether changes were synced by polling the API
      let lastResponse: boolean;
      // Delay first request to give the backend some time to sync the changes (otherwise our first needSync request would be pretty useless)
      timer(4000)
        .pipe(
          // What we actually want to do after the delay above
          concatMap(() => needSyncObservable.pipe(
            repeatWhen(notifier => {
              return notifier.pipe(
                takeWhile(() => lastResponse === true),
                delay(5000),
              );
            }),
            tap(response => lastResponse = response),
            take(10),
            filter(response => response === false)
          )),
        ).subscribe(() => {
          device.setNeedSync(false);
          this.logger.info(`Sync of device "${device.name}" successful`);
        });
    }
  }

  /**
   * Test whether actualVersionInt is in the given range. If a bound it is not given, it is ignored (=> true).
   * @param actualVersionInt Version string converted to integer (1.0.2.2 => 1022)
   * @param fromVersion Lower bound
   * @param toVersion Upper bound
   * @returns True if actualVersionInt is in the given range (including the bounds)
   */
  private isInVersionRange(actualVersion: string, fromVersion?: string, toVersion?: string): boolean {
    // Split version numbers from 1.2.3.4 in array containing [major, minor, patch, ...]
    const actualVersionArr: number[] = actualVersion.split('.').map((s) => Number.parseInt(s, 10));
    const fromVersionArr: number[] = fromVersion?.split('.').map((s) => Number.parseInt(s, 10)) ?? [];
    const toVersionArr: number[] = toVersion?.split('.').map((s) => Number.parseInt(s, 10)) ?? [];

    // fromVersion
    if (fromVersionArr.length > 0 && this.compareVersions(actualVersionArr, fromVersionArr) === -1) {
      return false;
    }

    // toVersion
    const res = this.compareVersions(actualVersionArr, toVersionArr);
    if (toVersionArr.length > 0 && (res === 0 || res === 1)) {
      return false;
    }

    // If we reach this point, we are in the range
    return true;
  }

  /**
   * Returns
   * - -1 if one < other
   * - 0 if one === other
   * - 1 if one > other
   * @param one Version array [major, minor, patch, ...]
   * @param other Version array [major, minor, patch, ...]
   */
  private compareVersions(one: number[], other: number[]): -1|0|1 {
    for (let i = 0; i < one.length; i++) {
      const o = other[i] ?? 0;
      if (one[i] < o) {
        return -1;
      } else if (one[i] > o) {
        return 1;
      }
    }

    if (other.length > one.length && other.slice(one.length).some((num) => num !== 0)) {
      // one is less than other if other contains any number except 0 at the indices greater than (one.length - 1)
      return -1;
    } else {
      return 0;
    }
  }
}
